import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, ImagePlus, Loader2, Pencil, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { adminApi, formatApiError } from '../../admin/AuthContext';
import { ImageUploader } from '../../admin/ImageUploader';
import { uploadImage } from '../../admin/upload';
import { MarkdownContent } from '../../components/MarkdownContent';

const CATEGORIES = ['Paper Chemicals', 'Dyes', 'Showers & Nozzles', 'Construction Chemicals', 'SS Wire Mesh', 'Company News'];
const EMPTY = { title: '', slug: '', excerpt: '', content: '', category: 'Paper Chemicals', tags: '', cover_image: '', author: 'VB Global Team', published: true };

const PostEditor = () => {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const inlineInputRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [inlineUploading, setInlineUploading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    adminApi.get(`/admin/blog/${id}`)
      .then(res => setForm({ ...res.data, tags: (res.data.tags || []).join(', ') }))
      .catch(err => { toast.error(formatApiError(err)); navigate('/admin'); })
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const insertInlineImage = async (file) => {
    if (!file) return;
    setInlineUploading(true);
    try {
      const res = await uploadImage(file);
      const snippet = `\n\n![${file.name.replace(/\.[^.]+$/, '')}](${res.url})\n\n`;
      const el = contentRef.current;
      const pos = el?.selectionStart ?? form.content.length;
      setForm(f => ({ ...f, content: f.content.slice(0, pos) + snippet + f.content.slice(pos) }));
      toast.success('Image inserted into article');
    } catch (err) { toast.error(formatApiError(err, 'Upload failed')); }
    finally { setInlineUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
    delete payload.id; delete payload.read_time; delete payload.created_at; delete payload.updated_at;
    try {
      if (isNew) {
        const res = await adminApi.post('/admin/blog', payload);
        toast.success('Post created');
        navigate(`/admin/posts/${res.data.id}`, { replace: true });
      } else {
        await adminApi.put(`/admin/blog/${id}`, payload);
        toast.success('Post saved');
      }
    } catch (err) { toast.error(formatApiError(err, 'Could not save post')); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-500 py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="post-editor-form">
      <div className="flex items-center justify-between gap-4">
        <Link to="/admin" className="inline-flex items-center text-sm text-[#8B2C3A] hover:underline" data-testid="post-editor-back"><ArrowLeft className="w-4 h-4 mr-1" /> All posts</Link>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <Switch checked={form.published} onCheckedChange={set('published')} data-testid="post-published-switch" />
            {form.published ? 'Published' : 'Draft'}
          </label>
          {!isNew && form.published && (
            <a href={`/blog/${form.slug}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-[#8B2C3A] inline-flex items-center gap-1"><Eye className="w-4 h-4" /> View</a>
          )}
          <Button type="submit" disabled={saving} className="bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white" data-testid="post-save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} {isNew ? 'Create post' : 'Save changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5 bg-white rounded-xl border border-gray-200 p-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input id="title" value={form.title} onChange={set('title')} required minLength={3} placeholder="e.g. How to reduce AKD consumption" data-testid="post-title-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="excerpt">Short summary * <span className="text-gray-400 font-normal">(shown on the blog list & Google)</span></Label>
            <Textarea id="excerpt" value={form.excerpt} onChange={set('excerpt')} required minLength={10} maxLength={500} rows={3} data-testid="post-excerpt-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="content">Article content * <span className="text-gray-400 font-normal">(Markdown: ## Heading, **bold**, - list)</span></Label>
              <div className="flex items-center gap-2">
                <Button type="button" size="sm" variant="outline" disabled={inlineUploading} onClick={() => inlineInputRef.current?.click()} data-testid="post-insert-image">
                  {inlineUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ImagePlus className="w-4 h-4 mr-1" />} Insert image
                </Button>
                <input ref={inlineInputRef} type="file" accept="image/*" className="hidden" data-testid="post-inline-image-input" onChange={(e) => { insertInlineImage(e.target.files?.[0]); e.target.value = ''; }} />
                <Button type="button" size="sm" variant={preview ? 'default' : 'outline'} onClick={() => setPreview(p => !p)} data-testid="post-preview-toggle" className={preview ? 'bg-[#8B2C3A] hover:bg-[#6B1C2A]' : ''}>
                  {preview ? <Pencil className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />} {preview ? 'Edit' : 'Preview'}
                </Button>
              </div>
            </div>
            {preview ? (
              <div className="border rounded-md p-5 min-h-[24rem] bg-gray-50" data-testid="post-preview"><MarkdownContent content={form.content} /></div>
            ) : (
              <Textarea ref={contentRef} id="content" value={form.content} onChange={set('content')} required minLength={20} rows={22} className="font-mono text-sm" data-testid="post-content-input" />
            )}
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            <ImageUploader value={form.cover_image} onChange={(url) => setForm(f => ({ ...f, cover_image: url }))} />
            <div className="space-y-2">
              <Label htmlFor="cover_url">…or paste an image URL</Label>
              <Input id="cover_url" value={form.cover_image} onChange={set('cover_image')} placeholder="https://…" data-testid="post-cover-url-input" />
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <select id="category" value={form.category} onChange={set('category')} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#8B2C3A]" data-testid="post-category-select">
                {[...new Set([...CATEGORIES, form.category].filter(Boolean))].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tags">Tags <span className="text-gray-400 font-normal">(comma separated)</span></Label>
              <Input id="tags" value={form.tags} onChange={set('tags')} placeholder="AKD, sizing, cost reduction" data-testid="post-tags-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">URL slug <span className="text-gray-400 font-normal">(auto from title if blank)</span></Label>
              <Input id="slug" value={form.slug} onChange={set('slug')} placeholder="how-to-reduce-akd-consumption" data-testid="post-slug-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="author">Author</Label>
              <Input id="author" value={form.author} onChange={set('author')} data-testid="post-author-input" />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default PostEditor;
